import React, { useState, useEffect, useContext } from 'react'
import env from 'config/env'
import { useLocation } from 'react-router-dom'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'

const firebaseConfig = {
    apiKey: env.FIREBASE.API_KEY,
    projectId: env.FIREBASE.PROJECT_ID,
    messagingSenderId: env.FIREBASE.MEASUREMENT_ID,
    appId: env.FIREBASE.APP_ID,
}

const app = initializeApp(firebaseConfig)

// 로컬 스토리지에서 레거시 'pushToken' 키 제거
localStorage.removeItem('pushToken')

interface INotificationContext {
    getDeviceToken: () => Promise<string>
}

interface PushData {
    event: string
    title: string
    content: string
    payload: {
        sendUserName: string
        chatRoomId: string
        clickId: string
    }
}

const NotificationContext = React.createContext<INotificationContext>(null!)

export default function NotificationProvider({ children }: React.PropsWithChildren<{}>) {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches
    const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)
    const location = useLocation()
    const pathname = location.pathname

    useEffect(() => {
        const initNotification = async () => {
            if ('serviceWorker' in navigator && isPWA) {
                try {
                    const registration = await navigator.serviceWorker.register(
                        '/firebase-messaging-sw.js'
                    )
                    setRegistration(registration)
                } catch (err) {
                    console.error('서비스워커 등록 중 오류 발생:', err)
                }
            }
        }
        initNotification()
    }, [isPWA])

    // foreground 알림 처리 훅
    // android 정상 작동
    // ios onMessage 수신 안됨
    useEffect(() => {
        if (!registration) {
            // 등록된 registration이 없으면 종료
            return
        }

        const messaging = getMessaging(app)

        console.log('onMessage 등록')
        const unsubscribe = onMessage(messaging, (payload) => {
            if (!payload.data?.pushData) return

            try {
                const pushData = JSON.parse(payload.data.pushData) as PushData
                const clickURL = `/chat/${pushData.payload.chatRoomId}`

                const isCurrentChatRoom = clickURL === pathname

                // 현재 보고있는 화면이 알림 이동 경로와 다를 경우에만 알림 표시
                if (!isCurrentChatRoom) {
                    void registration.showNotification(pushData.title, {
                        body: pushData.content,
                        icon: '/favicon.ico', // 아이콘 경로
                        data: {
                            url: clickURL,
                        },
                    })
                }
            } catch (err) {
                console.error('푸시 메시지 처리 중 오류 발생:', err)
            }
        })

        return () => {
            unsubscribe()
        }
    }, [registration, pathname])

    return (
        <NotificationContext.Provider value={{ getDeviceToken }}>
            {children}
        </NotificationContext.Provider>
    )
}

async function getDeviceToken() {
    // 권한이 허용된 후에 토큰을 가져옴
    const messaging = getMessaging(app)

    const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY
    const token = await getToken(messaging, { vapidKey })

    return token
}

export const useNotification = (): INotificationContext => {
    const context = useContext(NotificationContext)
    if (!context) {
        throw new Error('NotificationContext must be used within a NotificationProvider')
    }
    return context
}

// if (isPWA) {
//   document
//     .querySelector('meta[name="theme-color"]')
//     ?.setAttribute("content", "#EF528F");
// }
